import React, { useEffect, useState } from 'react';
import { X, ShoppingBag, Trash2 } from 'lucide-react';
import { useCart } from '../lib/cart';
import { absUrl } from '../lib/api';
import { formatMoney } from '../lib/utils';
import { useNavigate } from 'react-router-dom';

/**
 * Cart Preview Popup — Round 3.
 *
 * Retailers-style bottom sheet that lists every cart line as a compact row with
 * columns: Product | Flavour | Size | Qty | Price | Subtotal, plus a Grand Total
 * bar and two actions:
 *   - "Close Cart"  → closes only this preview; the underlying Choose Flavour
 *                     popup (if any) stays open so the user can continue.
 *   - "Confirm"     → closes the preview AND signals the caller to close the
 *                     flavour popup. Because navigation never occurred, the
 *                     homepage scroll position is preserved by the browser.
 *
 * Inline qty editing uses `type=number inputMode=numeric`. If the user clears
 * the field and blurs, the value reverts to the previous quantity.
 *
 * Props:
 *   onConfirm?: () => void  — invoked after Confirm; caller uses it to also
 *                             close the parent flavour popup if it is open.
 */
export default function CartPreviewSheet() {
    const { items, previewOpen, closePreview, confirmPreview, setQty, removeItem, total, count } = useCart();
    const navigate = useNavigate();

    // Lock body scroll while the preview is open — mirrors Retailers behavior.
    useEffect(() => {
        if (!previewOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [previewOpen]);

    // Keyboard escape closes preview only.
    useEffect(() => {
        if (!previewOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') closePreview(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [previewOpen, closePreview]);

    if (!previewOpen) return null;

    const grandTotal = total;

    const handleConfirm = () => {
        confirmPreview();
    };

    const goFullCart = () => {
        confirmPreview();
        navigate('/cart');
    };

    return (
        // z-[60] — sits above the flavour popup (z-50) so it visually stacks
        // on top of it while allowing the flavour popup to remain mounted.
        <div className="fixed inset-0 z-[60] animate-fade-in" onClick={closePreview} data-testid="cart-preview-overlay">
            <div className="absolute inset-0 bg-black/60" />
            <div
                className="absolute left-1/2 -translate-x-1/2 bottom-0 sm:bottom-6 w-full sm:w-[560px] max-h-[75vh] sm:max-h-[70vh] bg-sdf-ivory rounded-t-3xl sm:rounded-2xl overflow-hidden animate-slide-up shadow-popup flex flex-col
                            lg:bottom-auto lg:top-1/2 lg:-translate-y-1/2 lg:w-[800px] lg:max-w-[95vw] lg:max-h-[85vh]"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-label="Cart preview"
                data-testid="cart-preview-sheet"
            >
                {/* Header */}
                <div className="px-4 pt-3 pb-3 flex items-center justify-between border-b border-sdf-line bg-sdf-ivory">
                    <div className="flex items-center gap-2 min-w-0">
                        <ShoppingBag size={16} className="text-sdf-red flex-shrink-0" />
                        <div className="text-xs font-extrabold tracking-widest text-sdf-text">CART PREVIEW</div>
                        {count > 0 && (
                            <span className="bg-sdf-red text-white text-[10px] rounded-full px-2 py-0.5 font-bold" data-testid="cart-preview-count">{count}</span>
                        )}
                    </div>
                    <button onClick={closePreview} className="p-1.5 rounded-full hover:bg-black/5" aria-label="Close cart preview" data-testid="cart-preview-close-x"><X size={18} /></button>
                </div>

                {/* Column headings — always visible; compact */}
                <div className="grid px-3 py-1.5 border-b border-sdf-line bg-white/60 text-[9px] font-bold uppercase tracking-widest text-sdf-text-2 gap-1"
                     style={{ gridTemplateColumns: 'minmax(0, 1.5fr) 44px 96px 60px 64px 24px' }}>
                    <div>Product · Flavour</div>
                    <div>Size</div>
                    <div className="text-center">Qty</div>
                    <div className="text-right">Price</div>
                    <div className="text-right">Subtotal</div>
                    <div />
                </div>

                {/* Rows */}
                <div className="flex-1 overflow-y-auto px-2 py-2">
                    {items.length === 0 ? (
                        <div className="py-10 text-center text-sm text-sdf-text-2" data-testid="cart-preview-empty">
                            <ShoppingBag size={36} className="mx-auto text-neutral-300" />
                            <div className="mt-2 font-bold text-sdf-text text-sm">Your cart is empty</div>
                            <div className="mt-1 text-xs">Add a flavour from any product to get started.</div>
                        </div>
                    ) : (
                        <div className="space-y-1.5">
                            {items.map((it) => (
                                <CartRow
                                    key={`${it.product_id}|${it.variant_id}|${it.flavour_id}`}
                                    item={it}
                                    onQtyChange={(q) => setQty(it.product_id, it.variant_id, it.flavour_id, q)}
                                    onRemove={() => removeItem(it.product_id, it.variant_id, it.flavour_id)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Totals + actions */}
                <div className="border-t border-sdf-line bg-white safe-bottom">
                    <div className="px-4 py-2.5 flex items-center justify-between">
                        <div className="text-[11px] font-bold uppercase tracking-widest text-sdf-text-2">
                            Total Items: <span className="text-sdf-text" data-testid="cart-preview-total-items">{count}</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-sdf-text-2">Grand Total</div>
                            <div className="text-xl font-extrabold text-sdf-text" data-testid="cart-preview-grand-total">{formatMoney(grandTotal)}</div>
                        </div>
                    </div>
                    <div className="px-3 pb-3 pt-1 flex items-center gap-2">
                        <button
                            onClick={closePreview}
                            className="flex-1 rounded-full border border-sdf-line py-2.5 font-bold text-xs text-sdf-text hover:bg-neutral-50"
                            data-testid="cart-preview-close-btn"
                        >
                            CLOSE CART
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={items.length === 0}
                            className="flex-1 rounded-full bg-sdf-red text-white py-2.5 font-bold text-xs disabled:opacity-60"
                            data-testid="cart-preview-confirm-btn"
                        >
                            CONFIRM
                        </button>
                    </div>
                    <div className="px-3 pb-2 -mt-1 text-center">
                        <button
                            onClick={goFullCart}
                            className="text-[11px] font-bold tracking-wider text-sdf-red hover:underline"
                            data-testid="cart-preview-view-full"
                        >
                            View full cart &rsaquo;
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CartRow({ item, onQtyChange, onRemove }) {
    const [draft, setDraft] = useState(String(item.quantity));

    // Keep the input in sync when quantity changes externally (e.g. flavour popup +/-).
    useEffect(() => { setDraft(String(item.quantity)); }, [item.quantity]);

    const commit = () => {
        const trimmed = draft.trim();
        if (trimmed === '') {
            // Revert to previous value on empty input, per requirement.
            setDraft(String(item.quantity));
            return;
        }
        const n = parseInt(trimmed, 10);
        if (isNaN(n) || n < 0) {
            setDraft(String(item.quantity));
            return;
        }
        if (n !== item.quantity) onQtyChange(n);
    };

    const subtotal = (item.price || 0) * (item.quantity || 0);

    return (
        <div
            className="bg-white rounded-lg border border-sdf-line px-2 py-1.5 grid items-center gap-1 text-xs"
            style={{ gridTemplateColumns: 'minmax(0, 1.5fr) 44px 96px 60px 64px 24px' }}
            data-testid={`cart-row-${item.flavour_id}`}
        >
            {/* Product · Flavour (image + names) */}
            <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-md overflow-hidden bg-neutral-100 flex-shrink-0">
                    {item.image_url ? (
                        <img src={absUrl(item.image_url)} alt={item.product_name} className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-[9px] text-neutral-400">{item.product_name?.slice(0, 3)}</div>
                    )}
                </div>
                <div className="min-w-0">
                    <div className="font-bold text-sdf-text line-clamp-1 leading-tight" title={item.product_name}>{item.product_name}</div>
                    <div className="text-[10px] text-sdf-text-2 line-clamp-1 leading-tight" title={item.flavour_name}>{item.flavour_name}</div>
                </div>
            </div>

            {/* Size */}
            <div className="text-[11px] font-bold text-sdf-text">{item.size_label}</div>

            {/* Qty control (compact) */}
            <div className="flex items-center gap-0.5 justify-center">
                <button
                    type="button"
                    onClick={() => onQtyChange(Math.max(0, item.quantity - 1))}
                    className="w-6 h-6 rounded-full bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center text-xs font-bold"
                    aria-label="Decrease"
                    data-testid={`cart-row-minus-${item.flavour_id}`}
                >−</button>
                <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
                    onBlur={commit}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
                    className="w-9 h-6 border border-sdf-line rounded text-center text-[11px] font-bold outline-none focus:ring-1 focus:ring-sdf-red bg-white"
                    aria-label="Quantity"
                    data-testid={`cart-row-qty-${item.flavour_id}`}
                />
                <button
                    type="button"
                    onClick={() => onQtyChange(item.quantity + 1)}
                    className="w-6 h-6 rounded-full bg-sdf-red text-white hover:bg-sdf-red-hover flex items-center justify-center text-xs font-bold"
                    aria-label="Increase"
                    data-testid={`cart-row-plus-${item.flavour_id}`}
                >+</button>
            </div>

            {/* Price */}
            <div className="text-[11px] font-semibold text-sdf-text text-right" data-testid={`cart-row-price-${item.flavour_id}`}>{formatMoney(item.price || 0)}</div>

            {/* Subtotal */}
            <div className="text-[11px] font-extrabold text-sdf-text text-right" data-testid={`cart-row-subtotal-${item.flavour_id}`}>{formatMoney(subtotal)}</div>

            {/* Remove */}
            <button
                type="button"
                onClick={onRemove}
                className="p-1 text-red-600 hover:bg-red-50 rounded justify-self-end"
                aria-label="Remove"
                data-testid={`cart-row-remove-${item.flavour_id}`}
            >
                <Trash2 size={12} />
            </button>
        </div>
    );
}
